import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createDietaryPreference, updateDietaryPreference, fetchDietaryPreferenceById } from './dietaryPreferenceSlice';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import log from '../../logger';
import ReactForm from '../../components/ReactForm';
import { paths } from '../../routes/helpers';
import { getImageUrl, toLocalUploadPath } from '../../utils/urlUtils';
import ImageGallery from '../../components/ImageGallery';

const DietaryPreferenceCreateEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dietaryPreferenceId } = useParams();
  const loading = useSelector((state) => state.dietaryPreference.loading);
  const error = useSelector((state) => state.dietaryPreference.error);
  const dietaryPreference = useSelector((state) => 
    dietaryPreferenceId ? state.dietaryPreference.items.find(b => b._id === dietaryPreferenceId) : null
  );

  const [imageFiles, setImageFiles] = useState([]);
  const [existingImage, setExistingImage] = useState('');

  useEffect(() => {
    if (dietaryPreferenceId) {
      dispatch(fetchDietaryPreferenceById(dietaryPreferenceId));
    }
  }, [dietaryPreferenceId, dispatch]);

  useEffect(() => {
    if (dietaryPreference && dietaryPreference.image) {
      setExistingImage(getImageUrl(dietaryPreference.image));
    }
  }, [dietaryPreference]);

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    setImageFiles(files);
  };

  const handleRemoveImage = (index) => {
    setImageFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = () => {
    setExistingImage('');
  };

  const handleSubmit = async (formData) => {
    try {
      const processedData = new FormData();
      processedData.append('label', formData.label);
      processedData.append('status', parseInt(formData.status));
      
      if (imageFiles.length > 0) {
        processedData.append('image', imageFiles[0]);
      } else if (existingImage && dietaryPreferenceId) {
        // Keep existing image
        processedData.append('existingImage', toLocalUploadPath(existingImage));
      }

      if (dietaryPreferenceId) {
        await dispatch(updateDietaryPreference({ id: dietaryPreferenceId, formData: processedData })).unwrap();
        toast.success('Dietary preference updated successfully!');
        log.info('Dietary preference updated:', formData.label);
      } else {
        await dispatch(createDietaryPreference(processedData)).unwrap();
        toast.success('Dietary preference created successfully!');
        log.info('Dietary preference created:', formData.label);
      }
      navigate(paths.private.dietaryPreference.list());
    } catch (err) {
      toast.error(err.message || 'An error occurred');
      log.error('Error creating/updating dietary preference:', err);
    }
  };

  const fields = [
    {
      name: 'label',
      label: 'Label',
      type: 'text',
      required: true,
      placeholder: 'Enter dietary preference label'
    },
    {
      name: 'image',
      label: 'Image',
      type: 'file',
      accept: 'image/*',
      required: !dietaryPreferenceId || (!existingImage && imageFiles.length === 0),
      onChange: handleImageChange,
      preview: (
        <ImageGallery 
          existingImages={imageFiles.length > 0 ? [] : existingImage ? [existingImage] : []}
          imageFiles={imageFiles}
          onRemoveExisting={handleRemoveExistingImage}
          onRemoveNew={handleRemoveImage}
          loading={loading && dietaryPreference?.image && !existingImage}
        />
      )
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: "0",
      options: [
        { value: "0", label: 'Inactive' },
        { value: "1", label: 'Active' },
        { value: "2", label: 'Suspended' }
      ]
    }
  ];

  const defaultValues = dietaryPreference ? {
    label: dietaryPreference.label,
    image: [],
    status: dietaryPreference.status.toString()
  } : {
    label: '',
    image: [],
    status: "1"
  };

  log.info('Default values:', defaultValues);

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">
        {dietaryPreferenceId ? 'Edit Dietary Preference' : 'Create Dietary Preference'}
      </h2>
      <ReactForm
        fields={fields}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        defaultValues={defaultValues}
        submitText={dietaryPreferenceId ? 'Update Dietary Preference' : 'Create Dietary Preference'}
      />
    </div>
  );
};

export default DietaryPreferenceCreateEdit; 